import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { rendicionCampanaService } from '../../../services/api';
import type {
  MovimientoCampana,
  PlanCuentaCampana,
  ResumenCampana,
  TipoMovimientoCampana,
} from './types';

// =============================================================================
// Tipos y helpers
// =============================================================================

interface Props {
  rendicionId: string;
  tipo: TipoMovimientoCampana;
  readonly: boolean;
  snapshotMode?: boolean;
  onBack: () => void;
  onNext: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

interface FilaMov {
  codigo: string;
  denominacion: string;
  importe: number;
  esAutoCalculada: boolean;
}

function formatImporte(n: number | string | null | undefined): string {
  const v = Number(n ?? 0);
  return v.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// =============================================================================
// Componente
// =============================================================================

export default function Paso34Movimientos({
  rendicionId,
  tipo,
  readonly,
  snapshotMode,
  onBack,
  onNext,
  onDirtyChange,
}: Props) {
  const queryClient = useQueryClient();
  const esIngresos = tipo === 'INGRESO';
  const titulo = esIngresos ? 'Ingresos de Campaña' : 'Egresos de Campaña';

  const { data: planCuentas } = useQuery({
    queryKey: ['rendicion-campana-plan-cuentas'],
    queryFn: async () => {
      const res = await rendicionCampanaService.getPlanCuentas();
      return (res.data ?? []) as PlanCuentaCampana[];
    },
    staleTime: 1000 * 60 * 30, // catálogo casi estático
  });

  const { data: movsData, isLoading } = useQuery({
    queryKey: ['rendicion-campana-movimientos', rendicionId],
    queryFn: async () => {
      const res = await rendicionCampanaService.listMovimientos(rendicionId);
      return res.data as { movimientos: MovimientoCampana[]; resumen: ResumenCampana };
    },
    enabled: !snapshotMode,
    staleTime: snapshotMode ? Infinity : 0,
  });

  const cuentasDelPaso = useMemo(
    () => (planCuentas ?? []).filter((c) => c.tipo === tipo),
    [planCuentas, tipo],
  );

  const movPorCodigo = useMemo(() => {
    const map = new Map<string, MovimientoCampana>();
    for (const m of movsData?.movimientos ?? []) map.set(m.codigoCuenta, m);
    return map;
  }, [movsData]);

  // Estado local del input (codigo → string para evitar saltos al tipear)
  const [valores, setValores] = useState<Record<string, string>>({});
  const [inicializado, setInicializado] = useState(false);

  // Cuando llega el plan + movimientos, inicializo el estado una sola vez
  useEffect(() => {
    if (!planCuentas || !movsData || inicializado) return;
    const next: Record<string, string> = {};
    for (const c of cuentasDelPaso) {
      const m = movPorCodigo.get(c.codigo);
      next[c.codigo] = m ? Number(m.importe).toString() : '0';
    }
    setValores(next);
    setInicializado(true);
  }, [planCuentas, movsData, cuentasDelPaso, movPorCodigo, inicializado]);

  // Mantener actualizadas las cuentas auto-calculadas si cambian en el backend
  useEffect(() => {
    if (!inicializado || !movsData) return;
    setValores((prev) => {
      let cambio = false;
      const next = { ...prev };
      for (const c of cuentasDelPaso) {
        if (!c.esAutoCalculada) continue;
        const m = movPorCodigo.get(c.codigo);
        const nuevo = m ? Number(m.importe).toString() : '0';
        if (next[c.codigo] !== nuevo) {
          next[c.codigo] = nuevo;
          cambio = true;
        }
      }
      return cambio ? next : prev;
    });
  }, [movPorCodigo, cuentasDelPaso, inicializado, movsData]);

  // Detección de dirty
  const isDirty = useMemo(() => {
    if (!inicializado) return false;
    for (const c of cuentasDelPaso) {
      if (c.esAutoCalculada) continue;
      const valor = Number(valores[c.codigo] ?? 0);
      const m = movPorCodigo.get(c.codigo);
      const guardado = m ? Number(m.importe) : 0;
      if (Math.abs(valor - guardado) > 0.001) return true;
    }
    return false;
  }, [valores, movPorCodigo, cuentasDelPaso, inicializado]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const filas: FilaMov[] = useMemo(
    () =>
      cuentasDelPaso.map((c) => ({
        codigo: c.codigo,
        denominacion: c.denominacion,
        importe: Number(valores[c.codigo] ?? 0),
        esAutoCalculada: c.esAutoCalculada,
      })),
    [cuentasDelPaso, valores],
  );

  const totalDelPaso = useMemo(
    () => filas.reduce((acc, f) => acc + (Number.isFinite(f.importe) ? f.importe : 0), 0),
    [filas],
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = cuentasDelPaso
        .filter((c) => !c.esAutoCalculada)
        .map((c) => ({
          codigoCuenta: c.codigo,
          importe: Number(valores[c.codigo] ?? 0),
        }));
      return rendicionCampanaService.saveMovimientos(rendicionId, payload);
    },
    onSuccess: () => {
      toast.success('Movimientos guardados');
      queryClient.invalidateQueries({ queryKey: ['rendicion-campana-movimientos', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendicion-campana-validaciones', rendicionId] });
      onNext();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? 'No se pudo guardar'),
  });

  function handleSubmit() {
    if (readonly) {
      onNext();
      return;
    }
    if (!isDirty) {
      onNext();
      return;
    }
    saveMutation.mutate();
  }

  if (isLoading || !inicializado) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">
        Cargando...
      </div>
    );
  }

  const resumen = movsData?.resumen;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
          {titulo} — Por rubro
        </h2>

        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-sm text-blue-900 mb-4">
          {esIngresos ? (
            <>
              Cargá los <strong>montos totales por rubro</strong>. Las cuentas{' '}
              <code className="bg-blue-100 px-1 rounded">4.4.02</code> a{' '}
              <code className="bg-blue-100 px-1 rounded">4.4.05</code> se calculan automáticamente
              desde los aportantes (Paso 2) y no se pueden editar.
            </>
          ) : (
            <>
              Cargá los <strong>montos totales por rubro de gasto</strong>. No se requieren
              comprobantes individuales en esta instancia; el auditor puede solicitarlos durante la
              revisión.
            </>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Código</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rubro</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-44">Importe ($)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filas.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-sm text-gray-500">
                    Sin cuentas disponibles para este rubro.
                  </td>
                </tr>
              ) : (
                filas.map((f) => (
                  <tr key={f.codigo}>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500">{f.codigo}</td>
                    <td className="px-3 py-2 text-gray-700">
                      {f.denominacion}
                      {f.esAutoCalculada && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800">
                          AUTO desde aportantes
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        value={valores[f.codigo] ?? ''}
                        onChange={(e) =>
                          setValores((prev) => ({ ...prev, [f.codigo]: e.target.value }))
                        }
                        readOnly={readonly || f.esAutoCalculada}
                        className={`w-40 border rounded-lg px-3 py-1.5 text-sm font-mono text-right focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          readonly || f.esAutoCalculada
                            ? 'bg-gray-100 text-gray-700 border-gray-200 cursor-not-allowed'
                            : 'border-gray-300'
                        }`}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="bg-green-50">
                <td colSpan={2} className="px-3 py-3 text-right font-semibold text-gray-900">
                  TOTAL {esIngresos ? 'INGRESOS' : 'EGRESOS'}
                </td>
                <td className="px-3 py-3 text-right font-mono font-bold text-green-700">
                  $ {formatImporte(totalDelPaso)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Resumen de campaña — solo en el paso de egresos */}
      {!esIngresos && resumen && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Resultado de campaña</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ResumenBox label="Total ingresos" value={resumen.totalIngresos} color="text-blue-700" />
            <ResumenBox label="Total egresos" value={resumen.totalEgresos} color="text-rose-700" />
            <ResumenBox
              label={resumen.resultado >= 0 ? 'Superávit' : 'Déficit'}
              value={Math.abs(resumen.resultado)}
              color={resumen.resultado >= 0 ? 'text-green-700' : 'text-red-700'}
            />
          </div>
          <p className="mt-3 text-xs text-gray-500">
            El resultado se calcula con los datos guardados. Si modificás importes acá, guardalos
            primero para ver el resultado actualizado.
          </p>
        </div>
      )}

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-5 py-2 rounded-lg"
        >
          ← Anterior
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saveMutation.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
        >
          {saveMutation.isPending && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          )}
          {saveMutation.isPending
            ? 'Guardando...'
            : readonly
            ? 'Siguiente →'
            : isDirty
            ? 'Guardar y continuar →'
            : 'Continuar →'}
        </button>
      </div>
    </div>
  );
}

function ResumenBox({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
      <div className="text-xs font-semibold uppercase text-gray-600 mb-1">{label}</div>
      <div className={`text-lg font-bold font-mono ${color}`}>$ {formatImporte(value)}</div>
    </div>
  );
}
